import { useEffect, useRef } from 'react';

const services = [
  {
    title: 'Kitchen Remodeling',
    desc: 'Modern, functional kitchens built for daily living and long-term durability. From cabinetry and countertops to full layout transformations.',
    icon: 'ri-restaurant-fill',
    img: 'https://public.readdy.ai/ai/img_res/7cbd5c3758ffdc893cd4aec7db2d9eed.jpg',
    tag: null,
  },
  {
    title: 'Bathroom Remodeling',
    desc: 'Clean, efficient, high-end bathroom upgrades designed for comfort and long-term value. Tile, vanities, fixtures, and full renovations.',
    icon: 'ri-drop-fill',
    img: 'https://culphomeimprovement.com/wp-content/uploads/cache/2024/10/337398478_2317946648383461_7444748317339142647_n/3581404845.jpg',
    tag: null,
  },
  {
    title: 'Basement Finishing',
    desc: 'Transform your unfinished basement into a fully functional living space — family rooms, home bars, rec rooms, and more. Built to last.',
    icon: 'ri-building-2-fill',
    img: 'https://culphomeimprovement.com/wp-content/uploads/cache/2024/10/Adair-9/1156401355.jpg',
    tag: 'Most Popular',
  },
  {
    title: 'General Home Improvements',
    desc: 'Repairs, upgrades, and custom improvements tailored to your home. Interior and exterior work executed with precision and care.',
    icon: 'ri-hammer-fill',
    img: 'https://culphomeimprovement.com/wp-content/uploads/2024/10/419788193_939238311536112_4258605936431522899_n.jpg',
    tag: null,
  },
];

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.svc-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 110);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-32 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-18">
          <div>
            <p
              className="text-[#B8956A] text-[11px] font-bold uppercase tracking-[0.22em] mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              What We Do
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Complete Home<br />Improvement Solutions
            </h2>
          </div>
          <p
            className="text-[#777] text-base leading-relaxed max-w-xs md:mb-2"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Kitchen remodeling, bathroom upgrades, basement finishing, and general home improvements — all under one roof.
          </p>
        </div>

        {/* Cards Grid */}
        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((svc, i) => (
            <div
              key={i}
              className="svc-card group relative overflow-hidden cursor-pointer"
              style={{
                opacity: 0,
                transform: 'translateY(36px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
              }}
            >
              {/* Image */}
              <div className="w-full h-[300px] overflow-hidden">
                <img
                  src={svc.img}
                  alt={`${svc.title} by Culp Home Improvement`}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent"></div>

              {/* Tag */}
              {svc.tag && (
                <div
                  className="absolute top-5 left-5 bg-[#B8956A] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3.5 py-1.5 rounded-full"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {svc.tag}
                </div>
              )}

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#B8956A] rounded-sm flex-shrink-0">
                    <i className={`${svc.icon} text-white text-base`}></i>
                  </div>
                  <h3
                    className="text-white font-bold text-xl"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {svc.title}
                  </h3>
                </div>
                <p
                  className="text-white/70 text-sm leading-relaxed"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  {svc.desc}
                </p>
                <div
                  className="mt-5 flex items-center gap-1.5 text-[#C9A87C] text-[11px] font-bold uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Get an Estimate <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA after services */}
        <div className="text-center mt-16">
          <p
            className="text-[#888] text-sm mb-5"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Not sure where to start? We'll walk you through it.
          </p>
          <button
            onClick={() => scrollTo('contact')}
            className="inline-flex items-center gap-3 bg-[#1A1A1A] hover:bg-[#B8956A] text-white font-bold px-12 py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Request a Free Estimate <i className="ri-arrow-right-line text-[#C9A87C]"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
